import { useState, useEffect } from "react";
import { Link } from "@inertiajs/react";
import { SiGithub, SiX } from "@icons-pack/react-simple-icons";
import { LinkedinIcon } from "@/components/icons/LinkedinIcon";
import logoUrl from "@/assets/logo.svg";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";

const PRODUCT_LINKS = [
  { href: "/how-it-works", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/products/ec2", label: "EC2" },
  { href: "/products/auto-scaling-groups", label: "Auto Scaling Groups" },
  { href: "/products/ecs-fargate", label: "ECS Fargate" },
  { href: "/products/ebs", label: "EBS Volumes" },
];

const SERVICE_LINKS = [
  { href: "/services/cost-review", label: "AWS Cost Review" },
];

const RESOURCE_LINKS = [
  { href: "/docs", label: "Docs" },
  { href: "/articles", label: "Articles" },
  { href: "/support", label: "Support" },
];

const COMPANY_LINKS = [
  { href: "/about", label: "About" },
  { href: "/security", label: "Security" },
  { href: "/contact", label: "Contact" },
];

const LEGAL_LINKS = [
  { href: "/privacy-policy", label: "Privacy Policy" },
  { href: "/terms-of-service", label: "Terms of Service" },
];

export function Footer(): React.ReactElement {
  const [year, setYear] = useState("");

  useEffect(() => {
    setYear(new Date().getFullYear().toString());
  }, []);

  return (
    <footer className="border-t bg-background">
      {/* Top Area */}
      <div className="px-4 py-12 mx-auto max-w-7xl">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-7">
          {/* Logo + Blurb */}
          <div className="col-span-2 flex flex-col">
            <Link href="/" className="flex items-center space-x-2">
              <img
                src={logoUrl}
                alt="Infralyst Logo"
                width={32}
                height={32}
                className="w-auto h-8"
                loading="lazy"
              />
              <span className="text-lg font-bold tracking-tight font-brand">
                infralyst
              </span>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground max-w-sm">
              Infralyst finds AWS cost savings and turns them into ready to merge
              Terraform pull requests.
            </p>
            <a
              href="mailto:hello@infralyst.io"
              className="inline-block mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              hello@infralyst.io
            </a>
            <div className="flex gap-4 mt-auto pt-3">
              <a
                href="https://www.linkedin.com/company/infralyst"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <LinkedinIcon className="w-5 h-5" />
              </a>
              <a
                href="https://github.com/infralyst"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <SiGithub className="w-5 h-5" />
              </a>
              <a
                href="https://x.com/infralyst"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <SiX className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold mb-4">Product</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={CALENDLY_COST_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Book a demo
                </a>
              </li>
              {PRODUCT_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold mb-4">Services</h3>
            <ul className="space-y-3">
              {SERVICE_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold mb-4">Resources</h3>
            <ul className="space-y-3">
              {RESOURCE_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              {COMPANY_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://www.linkedin.com/company/infralyst/jobs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Careers
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              {LEGAL_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t">
        <div className="px-4 py-6 mx-auto max-w-7xl">
          <div className="flex flex-col items-center gap-4 text-xs text-muted-foreground md:flex-row md:justify-between">
            <div>
              © {year} Saankofi Limited (Hong Kong). Company number 79143950.
              All rights reserved.
            </div>
            <div>Built with ❤️ by the Infralyst team.</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
